import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Maximize2, Minimize2, PhoneOff, GripHorizontal, UserPlus, Check, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCall } from "@/contexts/CallContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FloatingCall() {
  const { activeCall, isMinimized, leaveCall, maximize, minimize } = useCall();
  const navigate = useNavigate();
  const location = useLocation();
  const [pos, setPos] = useState({ x: 16, y: window.innerHeight - 200 });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [joinedNames, setJoinedNames] = useState([]);
  const [joinBanner, setJoinBanner] = useState(null);
  const prevJoined = useRef(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const displayName = user.name || user.email || "Guest";

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Poll room for current participants
  useEffect(() => {
    if (!activeCall) return;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/video-calls/rooms/${activeCall.roomName}`);
        const current = res.data.participant_names || [];
        const others = current.filter((n) => n !== displayName);
        setJoinedNames(others);
        others.forEach((name) => {
          if (!prevJoined.current.has(name)) {
            setJoinBanner(name);
            setTimeout(() => setJoinBanner(null), 5000);
          }
        });
        prevJoined.current = new Set(others);
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [activeCall?.roomName, displayName]);

  useEffect(() => {
    setJoinedNames([]);
    prevJoined.current = new Set();
    setJoinBanner(null);
  }, [activeCall?.roomName]);

  if (!activeCall) return null;

  const dailyUrl = activeCall.meetingToken
    ? `${activeCall.dailyUrl}?t=${activeCall.meetingToken}`
    : activeCall.dailyUrl;

  const invitees = activeCall.inviteeNames || [];
  const waitingNames = invitees.filter(
    (n) => !joinedNames.includes(n) && n !== displayName
  );

  // FIX #1: Navigate FIRST so user doesn't land on blank page
  const handleEnd = async () => {
    const wasOnCallPage = location.pathname.startsWith("/call/");
    const token = localStorage.getItem("token");

    // Navigate away before clearing state
    if (wasOnCallPage) {
      navigate("/video-calls", { replace: true });
    }

    if (token) {
      try {
        await axios.post(
          `${API}/video-calls/rooms/${activeCall.roomName}/end`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch {}
    }
    leaveCall();
  };

  const handleMinimize = () => {
    minimize();
    if (location.pathname.startsWith("/call/")) {
      navigate("/video-calls", { replace: true });
    }
  };

  const handleExpand = () => {
    maximize();
    navigate(`/call/${activeCall.roomName}`);
  };

  const onPointerDown = (e) => {
    if (!isMinimized) return;
    dragging.current = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    setPos({
      x: Math.max(0, Math.min(window.innerWidth - 160, e.clientX - dragOffset.current.x)),
      y: Math.max(0, Math.min(window.innerHeight - 110, e.clientY - dragOffset.current.y)),
    });
  };
  const onPointerUp = () => { dragging.current = false; };

  // ═══════════════════════════════════════════════
  // FIX #2: Cleaner PiP — MINIMIZED MODE
  // ═══════════════════════════════════════════════
  if (isMinimized) {
    return (
      <div
        style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          width: 156,
          height: 106,
          zIndex: 9999,
          borderRadius: "14px",
          overflow: "hidden",
          background: "#000",
          touchAction: "none",
          boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          border: "2px solid rgba(255,255,255,0.15)",
        }}
        data-testid="floating-call"
      >
        {/* Drag handle — top edge */}
        <div
          className="absolute top-0 left-0 right-0 h-6 z-20 cursor-grab active:cursor-grabbing"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          data-testid="floating-call-drag"
        />

        {/* Video iframe */}
        <iframe
          src={dailyUrl}
          allow="camera; microphone; fullscreen; display-capture; autoplay; screen-wake-lock"
          style={{ width: "100%", height: "100%", border: "none", display: "block" }}
          data-testid="daily-iframe"
        />

        {/* Two-button bar — bottom */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-3 py-1.5 z-20 bg-black/60">
          <button
            onClick={handleExpand}
            className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors"
            title="Expand"
            data-testid="floating-expand-btn"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleEnd}
            className="w-7 h-7 rounded-full bg-red-500/80 flex items-center justify-center text-white hover:bg-red-500 transition-colors"
            title="Leave call"
            data-testid="floating-end-btn"
          >
            <PhoneOff className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════
  // FULL-SCREEN MODE
  // ═══════════════════════════════════════════════
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 9998, background: "#0F0F23", display: "flex", flexDirection: "column" }}
      data-testid="full-call-overlay"
    >
      {/* Header bar */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b border-white/10 flex-shrink-0"
        style={{ background: "linear-gradient(90deg, #1A1A2E, #16213E)" }}
      >
        <div className="flex items-center gap-3">
          <Button
            variant="ghost" size="sm"
            onClick={handleMinimize}
            className="text-white/70 hover:text-white hover:bg-white/10 p-2"
            data-testid="call-minimize-btn"
          >
            <Minimize2 className="w-5 h-5" />
          </Button>
          <h1 className="text-sm font-semibold text-white" data-testid="call-room-name">Video Call</h1>
          {joinedNames.length > 0 && (
            <span className="flex items-center gap-1 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full" data-testid="participant-count">
              <Users className="w-3 h-3" /> {joinedNames.length + 1}
            </span>
          )}
        </div>
        <Button
          size="sm" onClick={handleEnd}
          className="bg-red-500 hover:bg-red-600 text-white text-xs px-3"
          data-testid="leave-call-btn"
        >
          <PhoneOff className="w-3 h-3 mr-1" /> Leave
        </Button>
      </div>

      {/* Join banner */}
      {joinBanner && (
        <div
          className="absolute top-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-2.5 rounded-2xl shadow-xl"
          style={{ background: "linear-gradient(135deg, #10B981, #059669)" }}
          data-testid="join-banner"
        >
          <UserPlus className="w-4 h-4 text-white" />
          <span className="text-white font-semibold text-sm">{joinBanner} joined</span>
        </div>
      )}

      {/* FIX #3: Participant panel — BOTTOM-LEFT (doesn't cover self-view) */}
      {(invitees.length > 0 || joinedNames.length > 0) && (
        isMobile ? (
          <div
            className="flex items-center gap-2 px-3 py-1.5 border-b border-white/5 overflow-x-auto flex-shrink-0"
            style={{ background: "rgba(15, 15, 35, 0.6)" }}
            data-testid="participant-strip-mobile"
          >
            {waitingNames.map((name) => (
              <span key={`mw-${name}`} className="inline-flex items-center gap-1 bg-amber-500/15 text-amber-300 text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                <Clock className="w-3 h-3" /> {name}
              </span>
            ))}
            {joinedNames.map((name) => (
              <span key={`mj-${name}`} className="inline-flex items-center gap-1 bg-green-500/15 text-green-300 text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                <Check className="w-3 h-3" /> {name}
              </span>
            ))}
          </div>
        ) : (
          <div
            className="absolute bottom-4 left-3 z-40 w-48 rounded-2xl overflow-hidden"
            style={{ background: "rgba(15, 15, 35, 0.85)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.1)" }}
            data-testid="participant-panel"
          >
            <div className="px-3 py-1.5 border-b border-white/10">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/40">Participants</p>
            </div>
            <div className="p-1.5 space-y-0.5 max-h-40 overflow-y-auto">
              {joinedNames.map((name) => (
                <div key={`j-${name}`} className="flex items-center gap-2 px-2 py-1 rounded-lg" data-testid={`participant-joined-${name}`}>
                  <div className="w-4 h-4 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-2.5 h-2.5 text-green-400" />
                  </div>
                  <span className="text-white text-[11px] font-medium truncate">{name}</span>
                  <span className="text-green-400 text-[9px] ml-auto flex-shrink-0">Joined</span>
                </div>
              ))}
              {waitingNames.map((name) => (
                <div key={`w-${name}`} className="flex items-center gap-2 px-2 py-1 rounded-lg" data-testid={`participant-waiting-${name}`}>
                  <div className="w-4 h-4 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-2.5 h-2.5 text-amber-400 animate-pulse" />
                  </div>
                  <span className="text-white/60 text-[11px] font-medium truncate">{name}</span>
                  <span className="text-amber-400/70 text-[9px] ml-auto flex-shrink-0">Waiting</span>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Daily.co iframe */}
      <div key="iframe-wrap" style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <iframe
          src={dailyUrl}
          allow="camera; microphone; fullscreen; display-capture; autoplay; screen-wake-lock"
          style={{ width: "100%", height: "100%", border: "none", display: "block" }}
          data-testid="daily-iframe"
        />
      </div>
    </div>
  );
}
